
import { createContext, ReactNode, useContext } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiV2 } from '../services/api';
import LocalStorageService from '../services/storage';

const localStorageService = LocalStorageService();

interface Provider {
    id_providers: string;
    type: string;
    providers_name: string;
    description: string;
    isactive?: boolean;

}
type ProviderInput = Omit<Provider, 'id_providers'>

interface ProvidersProvidersProps {
    children: ReactNode;
}

interface ResponseProps {
    success: boolean;
    message: string;
}

interface ProvidersContextData {
    createProvider: (provider: ProviderInput) => Promise<ResponseProps>;
    updateProvider: (provider: Provider) => Promise<ResponseProps>;
    updateOnDelete: (id: string | number) => Promise<ResponseProps>;
}

interface UseProvidersOptions {
    loadProviders?: boolean;
}

interface UseProvidersData extends ProvidersContextData {
    providers: Provider[];
}

const ProvidersContext = createContext<ProvidersContextData>(
    {} as ProvidersContextData
);

const providersQueryKey = ['providers'];
const emptyProviders: Provider[] = [];

function hasToken() {
  return Boolean(localStorageService.getIdToken());
}

async function fetchProviders() {
  const response = await apiV2.get('providers');
  return Array.isArray(response?.data) ? response.data : [];
}

export function ProvidersProvider({ children }: ProvidersProvidersProps) {
  const queryClient = useQueryClient();

  function invalidateProviders() {
    queryClient.invalidateQueries({ queryKey: providersQueryKey });
  }

  async function updateOnDelete(id: string | number) {
    const response = await apiV2.delete(`/providers/${id}`);
    if (response.status === 200) {
      invalidateProviders();
      return {
        success: true,
        message: "Removido com sucesso!"
      };
    }
    return {
      success: false,
      message: "Erro ao remover."
    };
  }

  async function createProvider(providerInput: ProviderInput) {
    const response = await apiV2.post('/providers', providerInput);
    if (response.status === 201) {
      invalidateProviders();
      return {
        success: true,
        message: "Adicionado com sucesso!"
      };
    }
    return {
      success: false,
      message: "Erro ao adicionar."
    };

  }

  async function updateProvider(provider: Provider) {
    const response = await apiV2.put(`/providers/${provider.id_providers}`, provider);
    if (response.status === 204) {
      invalidateProviders();
      return {
        success: true,
        message: "Editado com sucesso!"
      };
    }
    return {
      success: false,
      message: "Erro ao editar."
    };
  }

  return (
    <ProvidersContext.Provider value={{ createProvider, updateProvider, updateOnDelete }}>
      {children}
    </ProvidersContext.Provider>
  );
}

export function UseProviders(options: UseProvidersOptions = {}): UseProvidersData {
  const context = useContext(ProvidersContext);
  const { loadProviders = false } = options;

  const providersQuery = useQuery({
    queryKey: providersQueryKey,
    queryFn: fetchProviders,
    enabled: loadProviders && hasToken(),
    staleTime: 5 * 60 * 1000
  });

  return {
    ...context,
    providers: providersQuery.data || emptyProviders
  };
}
